import InfiniteScroll from 'react-infinite-scroll-component';
import { Role, useGetAppSelectorInfo, useLogout } from 'auth';
import classNames from 'classnames';
import { Fragment, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SignUpPagesWrapper } from '@/modules/auth/components/SignUpPagesWrapper.tsx';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import {
  APPLY_REGISTER_CLASS_ID,
  APPLY_REGISTER_ID,
  APPLY_REGISTER_PRIVATE_ID,
  TEST_PRIVATE_ID,
} from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';
import { EntityListItem } from '@/modules/entity/components/items/EntityListItem.tsx';
import {
  CURRENT_ENTITY_CREATING_KEY,
  useCurrentUserEntitiesQuery,
  useRegisterByPrivateIdQuery,
} from '@/modules/entity/queries/entity.tsx';
import { entityPaths } from '@/modules/entity/routes.tsx';
import { EmptyListState, PopoverMenu, Skeleton, Spinner } from 'ui';
import { GridDotsBlackIcon, PlusBlueIcon } from 'ui/icons';

const SelectEntityPageNew = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: currentUser } = useCurrentUserQuery();
  const onLogout = useLogout({ onRemoveQueries: () => queryClient.clear() });
  const applyRegisterPrivateId = params.get(APPLY_REGISTER_PRIVATE_ID) || '';
  const appSelectorInfo = useGetAppSelectorInfo();

  const {
    data: applyRegister,
    isLoading: isLoadingApplyRegister,
    isError: isErrorApplyRegister,
  } = useRegisterByPrivateIdQuery(
    applyRegisterPrivateId,
    TEST_PRIVATE_ID.length !== applyRegisterPrivateId?.length,
  );

  const {
    data,
    isLoading: isEntityListLoading,
    hasNextPage,
    fetchNextPage,
  } = useCurrentUserEntitiesQuery({
    page: 1,
    page_size: 500,
    invalidateQueriesCurrentEntity: true,
  });

  const handleClickNewEntity = useCallback(() => {
    queryClient.removeQueries([CURRENT_ENTITY_CREATING_KEY]);
    params.forEach((_, key) => {
      if (
        key !== APPLY_REGISTER_ID &&
        key !== APPLY_REGISTER_CLASS_ID &&
        key !== APPLY_REGISTER_PRIVATE_ID
      ) {
        params.delete(key);
      }
    });
    setParams(params, { replace: false });
    navigate(
      { pathname: entityPaths.static.abnLookupEntity, search: params.toString() },
      {
        replace: false,
      },
    );
  }, [navigate, params, setParams]);

  const mainAction = useMemo(
    () => ({
      name: 'Logout',
      onClick: () => onLogout(window.location.origin),
      helpText: (
        <div className="flex gap-4">
          <div className="space-y-0.5 max-w-full">
            <div className="text-sm">
              {currentUser?.first_name} {currentUser?.last_name}
            </div>
            <div
              className="text-xs text-[rgba(23,23,23,0.64)] break-words"
              style={{ wordBreak: 'break-word' }}
            >
              {currentUser?.email}
            </div>
          </div>
          <PopoverMenu
            classes={{
              container: '!w-auto z-10 !mt-[9px]',
              panel: 'w-[230px] !left-[-45px] shadow-lg border bg-white',
            }}
            button={<img src={GridDotsBlackIcon} alt="app selector" />}
            items={appSelectorInfo.items}
          />
        </div>
      ),
    }),
    [
      appSelectorInfo.items,
      currentUser?.email,
      currentUser?.first_name,
      currentUser?.last_name,
      onLogout,
    ],
  );

  const title = useMemo(() => {
    if (isLoadingApplyRegister && TEST_PRIVATE_ID.length === applyRegisterPrivateId?.length) {
      return (
        <span className="w-full mb-4 lg:mb-10">
          <Skeleton height={136} />
        </span>
      );
    }

    if (applyRegister?.data?.name && !isErrorApplyRegister) {
      return (
        <>
          <p
            className="w-full text-3xl md:text-5xl md:leading-[56px] font-semibold font-serif mb-4 lg:mb-10 break-words"
            style={{ wordBreak: 'break-word' }}
          >
            You were invited to invest to{' '}
            <span className="text-blue-700">
              {applyRegister?.data?.name?.replace(/(fund|company)/i, '')}
            </span>{' '}
            {applyRegister?.data?.type}
          </p>
          <p className="w-full text-start mb-4 lg:mb-10">
            Select account you want to use to invest
          </p>
        </>
      );
    }

    return <span></span>;
  }, [applyRegister?.data, isLoadingApplyRegister, isErrorApplyRegister, applyRegisterPrivateId]);

  if (isEntityListLoading) {
    return (
      <SignUpPagesWrapper>
        <div className="w-full flex items-center justify-center h-full flex-col">
          <Spinner size="lg" />
          <p className="text-md leading-[32px] mt-4">Loading your entities...</p>
        </div>
      </SignUpPagesWrapper>
    );
  }

  return (
    <SignUpPagesWrapper
      className="sm:px-10 px-4 justify-start gap-8 overflow-y-hidden"
      mainAction={mainAction}
    >
      <div className="w-full flex flex-1 items-center justify-center h-full">
        <div className="max-w-[660px] w-full h-full sm:px-6 px-0 flex flex-col items-start justify-center">
          <EmptyListState
            title="No open account yet."
            description="Add your first account and start investing with Compli One"
            action={{
              disabledHint: 'You must be invited to the registry to create an investor',
              className: 'w-fit',
              content: 'Create investor account',
              onClick: handleClickNewEntity,
            }}
            isLoading={isEntityListLoading}
            isSearchUsed={false}
            itemsCount={data?.pages?.[0]?.total ?? 0}
          />

          {!!data?.pages?.[0]?.total && (
            <div className="w-full h-full flex flex-col items-center justify-center">
              {title}
              <div
                className={classNames('w-full overflow-y-auto pr-2 sm:max-h-[40%]', {
                  'max-h-[52dvh]': applyRegister?.data?.name && !isErrorApplyRegister,
                  'max-h-[70dvh]': !(applyRegister?.data?.name && !isErrorApplyRegister),
                })}
              >
                <InfiniteScroll
                  next={fetchNextPage}
                  dataLength={data?.pages?.length ?? 0}
                  hasMore={!!hasNextPage}
                  style={{ overflowY: 'auto' }}
                  loader={
                    <div className="flex justify-center items-center h-16 w-full">
                      <Spinner size="md" variant="primary" />
                    </div>
                  }
                  className="w-full h-full flex flex-col items-center justify-center gap-2 pb-1"
                >
                  {data?.pages?.map((entities, i) => (
                    <Fragment key={i}>
                      {entities?.data?.map(entity => (
                        <EntityListItem key={'entity_' + entity.id} entity={entity} />
                      ))}
                    </Fragment>
                  ))}
                </InfiniteScroll>
              </div>
              <div className="w-full pb-1 pt-4 pr-2">
                <button
                  onClick={handleClickNewEntity}
                  className="w-full flex justify-start items-center p-4 space-x-4 rounded-lg border border-blue-700 cursor-pointer hover:bg-gray-100"
                >
                  <img src={PlusBlueIcon} alt="Loading" />
                  <p className="text-base text-blue-700">Add new account</p>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </SignUpPagesWrapper>
  );
};

export default withPrivateAccess(SelectEntityPageNew, Role.SR_INVESTOR);
